import { useService } from '@policoders/di';
import {
  BreadcrumbService,
  CellType,
  TColumn,
  TTableActions,
  TTableLineActions,
} from '@policoders/ds';
import { useEffect, useState } from 'react';

import dateHelper from '../../../../core/helpers/dateHelper';
import formatHelper from '../../../../core/helpers/formatHelper';
import { TransactionOptions, TransactionType } from '../../types/transaction.type';
import CashBookForm from './cash-book-form';
import { CashBook } from './cash-book.model';
import { CashBookStore } from './cash-book.store';

export const useCashBook = () => {
  const store = useService<CashBookStore>('CashBookStore');
  const [lines, setLines] = useState<CashBook[]>([]);

  const breadcrumb = useService<BreadcrumbService>('BreadcrumbService');
  useEffect(() => {
    breadcrumb.set([
      { label: "Chef's Assistant", value: '/chefs-assistant' },
      { label: 'Livro caixa', value: '/chefs-assistant/cash-book' },
    ]);
  }, [breadcrumb]);

  useEffect(() => {
    store.loadList();
    var subscriber = store.state$.subscribe(state => {
      if (!state.transactions) return;
      const calculated = state.transactions.reduce(
        (p, t) => {
          const transactionTypeFormated = TransactionOptions.find(
            x => x.value === t.transactionType,
          );
          const depositAmount = t.transactionType === TransactionType.deposit ? t.amount : 0;
          const withdrawalAmount = t.transactionType === TransactionType.withdrawal ? t.amount : 0;
          p.sum += depositAmount;
          p.sum -= withdrawalAmount;
          const transaction = {
            ...t,
            dateFormated: dateHelper.format(dateHelper.toDate(t.date), 'dd/MM/yyyy'),
            transactionTypeFormated: transactionTypeFormated?.label,
            depositAmount: {
              type: 'secondary',
              value: depositAmount > 0 && formatHelper.currency(depositAmount),
            },
            withdrawalAmount: {
              type: 'secondary',
              value: withdrawalAmount > 0 && '-' + formatHelper.currency(withdrawalAmount),
            },
            amountFormated: {
              type: t.transactionType === TransactionType.deposit ? 'success' : 'danger',
              value: formatHelper.currency(t.amount),
            },
            resultAmount: {
              type: p.sum >= 0 ? 'success' : 'danger',
              value: formatHelper.currency(p.sum),
            },
          };
          p.t.push(transaction);
          return p;
        },
        { t: [], sum: 0 } as { t: CashBook[]; sum: number },
      );
      setLines(calculated.t.sort((a, b) => (a.transactionCode > b.transactionCode ? -1 : 1)));
    });
    return () => {
      subscriber.unsubscribe();
    };
  }, [store]);

  const handleSave = async () => {
    store.save();
  };

  const handleNew = () => {
    const model = new CashBook();
    store.updateCurrent(model);
    store.ui.modal
      .config({
        title: 'novo lançamento',
        size: 'large',
        buttonOkLabel: 'Criar',
        buttonOkAction: handleSave,
        children: () => (
          <CashBookForm
            data={model}
            onChange={state => {
              store.updateCurrent(state);
            }}
          />
        ),
      })
      .open();
  };

  const handleEdit = (line: CashBook) => {
    const model = lines.find(x => x.id === line.id);
    if (!model) return;
    store.ui.modal
      .config({
        title: `editar lançamento (${line.id.split('-')[0]})`,
        size: 'large',
        buttonOkLabel: 'Salvar',
        buttonOkAction: handleSave,
        children: () => (
          <CashBookForm
            data={model}
            onChange={state => {
              store.updateCurrent(state);
            }}
          />
        ),
      })
      .open();
  };

  const handleDelete = (line: CashBook) => {
    if (window.confirm('excluir produto?')) store.delete(line.id);
  };

  const tActions: TTableLineActions[] = [{ label: 'excluir', onClick: handleDelete }];

  const tColumns: TColumn[] = [
    { field: 'transactionCode', title: '#', columnClass: 'd-none d-md-table-cell' },
    { field: 'dateFormated', title: 'Data', columnClass: 'd-none d-lg-table-cell' },
    { field: 'transactionTypeFormated', title: 'Tipo', columnClass: 'd-none d-md-table-cell' },
    {
      field: 'notes',
      title: 'Descrição',
    },
    {
      field: 'amountFormated',
      title: 'Valor',
      type: CellType.BADGE,
      columnClass: 'd-table-cell d-md-none text-end',
    },
    {
      field: 'depositAmount',
      title: 'Entrada',
      columnClass: 'd-none d-md-table-cell text-end',
    },
    {
      field: 'withdrawalAmount',
      title: 'Saída',
      columnClass: 'd-none d-md-table-cell text-end',
    },
    {
      field: 'resultAmount',
      title: 'Saldo',
      type: CellType.BADGE,
      columnClass: 'd-none d-sm-table-cell text-end',
    },
  ];

  const tHeaderButtons: TTableActions = {
    buttonNew: {
      label: 'Novo',
      action: handleNew,
    },
  };

  return {
    lines,
    tActions,
    tColumns,
    tHeaderButtons,
    handleEdit,
  };
};
